import { ReactQueryStaleTime } from '@openx/constants/react-query';
import type { SegmentsOptions } from '@openx/types/targeting/customVariables';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  SearchOptionsByPathDocument,
  type SearchOptionsByPathQuery,
  type SearchOptionsByPathQueryVariables,
} from '../../../graphql/query/options/SearchOptionsByPath.generated';
import { searchOptionsByPathQueryKey } from '../../../hooks/useSearchOptionsByPathQuery';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types';
import { mapSegmentsOptions } from '../../targeting/utils/mapSegmentsOptions';

// searchOptionsByPath caps `limit` at 500 on the API side
export const SEARCH_OPTIONS_MAX_LIMIT = 500;

const CONTEXTUAL_SEGMENTS_PATH = 'deal.package.targeting.contextual.segments';

type UseSelectedContextualSegmentsFetchParams = {
  accountId: string | undefined;
  providerIds: string[];
  segmentIds: string[];
};

type UseSelectedContextualSegmentsFetchResult = {
  error: Error | null;
  loading: boolean;
  segments: SegmentsOptions;
};

const EMPTY_SEGMENTS: SegmentsOptions = [];

// A segment id can match once per provider, so each chunk is sized to fit all its matches in a single page
const getSegmentIdsChunks = (segmentIds: string[], providerIds: string[]): string[][] => {
  const chunkSize = Math.max(1, Math.floor(SEARCH_OPTIONS_MAX_LIMIT / Math.max(providerIds.length, 1)));

  return Array.from({ length: Math.ceil(segmentIds.length / chunkSize) }, (_, index) =>
    segmentIds.slice(index * chunkSize, (index + 1) * chunkSize)
  );
};

/**
 * Fetches metadata (including prices) of all selected contextual segments, splitting the lookup into requests
 * that each fit into a single page of searchOptionsByPath results.
 */
export const useSelectedContextualSegmentsFetch = ({
  accountId,
  providerIds,
  segmentIds,
}: UseSelectedContextualSegmentsFetchParams): UseSelectedContextualSegmentsFetchResult => {
  const variablesList = useMemo<SearchOptionsByPathQueryVariables[]>(
    () =>
      accountId
        ? getSegmentIdsChunks(segmentIds, providerIds).map(segmentIdsChunk => ({
            path: CONTEXTUAL_SEGMENTS_PATH,
            filter: {
              account_id: accountId,
              provider_id: providerIds.join(',') || undefined,
              segment_id: segmentIdsChunk.join(','),
              limit: SEARCH_OPTIONS_MAX_LIMIT,
              offset: 0,
            },
          }))
        : [],
    [accountId, providerIds, segmentIds]
  );

  return useQueries({
    queries: variablesList.map(variables => ({
      queryFn: ({ client }): Promise<SearchOptionsByPathQuery> =>
        getGraphQLRequestFromClient(client)(SearchOptionsByPathDocument, variables),
      queryKey: searchOptionsByPathQueryKey(variables),
      staleTime: ReactQueryStaleTime.MINUTES_15,
    })),
    combine: results => {
      if (results.length === 0) {
        return { error: null, loading: false, segments: EMPTY_SEGMENTS };
      }

      const loading = results.some(result => result.isLoading);
      const requestError = results.find(result => result.error)?.error ?? null;
      // a truncated page would hide segments, so their prices could not be verified
      const isIncomplete = results.some(result => result.data?.searchOptionsByPath.has_next_page);

      return {
        error: requestError ?? (isIncomplete ? new Error('Selected contextual segments lookup is incomplete') : null),
        loading,
        segments: loading
          ? EMPTY_SEGMENTS
          : results.flatMap(result => mapSegmentsOptions(result.data?.searchOptionsByPath.results ?? [])),
      };
    },
  });
};
